import { useNavigate, useRouterState } from '@tanstack/react-router';
import { memo, useEffect, useMemo, useState } from 'react';
import { Selection } from 'react-aria-components';

import { Button, Heading, Menu, MenuItem, Separator, Text } from '@/components/core';
import { ChevronIcon, CreditCardIcon, PlansIcon, UserIcon, UsersIcon } from '@/components/icons';
import { NavigationBar as NavBar } from '@/components/ui';
import { useGetAccessLevel, useRemainingDaysOfTrial } from '@/hooks';
import { AccessLevel } from '@/utils';

import { UpgradeCard } from '../agents/UpgradeCard.tsx';

export const INavigationBar = () => {
  const navigate = useNavigate();
  const routerState = useRouterState();

  const routeHandler = useMemo(() => {
    const profileRoute = { to: '/settings/profile' };
    const workspaceRoute = { to: '/settings/workspace' };
    const billingRoute = { to: '/settings/billing' };
    const plansRoute = { to: '/settings/plans' };

    return {
      profile: {
        id: 'profile',
        navigate: async () => navigate(profileRoute),
        isSelected: routerState.location.pathname.endsWith('profile'),
      },
      workspace: {
        id: 'workspace',
        navigate: async () => navigate(workspaceRoute),
        isSelected: routerState.location.pathname.endsWith('workspace'),
      },
      billing: {
        id: 'billing',
        navigate: async () => navigate(billingRoute),
        isSelected: routerState.location.pathname.endsWith('billing'),
      },
      plans: {
        id: 'plans',
        navigate: async () => navigate(plansRoute),
        isSelected: routerState.location.pathname.endsWith('plans'),
      },
    };
  }, [navigate, routerState.location.pathname]);

  const { hasUpgraded } = useRemainingDaysOfTrial();

  const selectedKeysFromRouteHandler = useMemo(
    () =>
      Object.values(routeHandler)
        .filter((route) => route.isSelected)
        .map((route) => route.id),
    [routeHandler]
  );

  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set(selectedKeysFromRouteHandler));

  useEffect(() => {
    setSelectedKeys(new Set(selectedKeysFromRouteHandler));
  }, [selectedKeysFromRouteHandler]);

  const { hasAccessLevel } = useGetAccessLevel();

  return (
    <NavBar>
      <div className="flex flex-row items-center gap-2 px-2 py-2">
        <Button
          variant="plain"
          onPress={async () => {
            await navigate({ to: '/agents' });
          }}
        >
          <ChevronIcon rotation="left" />
        </Button>
        <Heading>Account settings</Heading>
      </div>
      <Separator orientation="horizontal" />
      <Menu selectionMode="single" selectedKeys={selectedKeys} aria-label="Settings">
        <MenuItem id={routeHandler.profile.id} onAction={routeHandler.profile.navigate}>
          <UserIcon />
          <Text>Profile</Text>
        </MenuItem>

        {hasAccessLevel(AccessLevel.Reviewer) && (
          <MenuItem id={routeHandler.workspace.id} onAction={routeHandler.workspace.navigate}>
            <UsersIcon />
            <Text>Workspace</Text>
          </MenuItem>
        )}

        {hasAccessLevel(AccessLevel.Admin) && (
          <>
            <MenuItem id={routeHandler.billing.id} onAction={routeHandler.billing.navigate}>
              <CreditCardIcon />
              <Text>Billing</Text>
            </MenuItem>
            <MenuItem id={routeHandler.plans.id} onAction={routeHandler.plans.navigate}>
              <PlansIcon />
              <Text>Plans</Text>
            </MenuItem>
          </>
        )}
      </Menu>

      <div className="flex-grow" />
      <Separator orientation="horizontal" />

      {!hasUpgraded && <UpgradeCard organizationId="me" />}

      <Text className="mb-2 self-end px-4 text-gray-300">
        version: {(import.meta.env.VITE_CURRENT_COMMIT_SHA ?? '').substring(0, 7) || 'dev'}
      </Text>
    </NavBar>
  );
};

export const NavigationBar = memo(INavigationBar);
